import { debounce, memoize } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import { API_ENDPOINT, globalCache } from '@sbrc/utils';

import type { ConnectionStatus } from './ConnectionStatus';
import { RoutineRunnerHandle } from './RoutineRunnerHandle';

const log = makeNamespacedLog('WebSocketRoutineRunnerHandle');

const MAX_FAILED_ATTEMPTS = 3;
const MAX_TOKEN_RETRIES = 10;

/**
 * Exponential-backoff schedule for reconnects after a non-4401 close.
 * Delay for attempt N (0-indexed) is `min(BASE * 2^N, CAP)`: 1s, 2s, 4s,
 * 8s, 16s, then 30s thereafter. Counter resets to 0 after a successful
 * authentication so that brief-network-blip recovery starts at 1s again.
 */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;

/**
 * WebSocket close code that botman sends when the JWT is rejected
 * (auth handshake failure). When the routine-runner WS closes with this
 * code we must NOT reconnect — the dead token would just be rejected
 * again, storming botman's rate limit. PrivatePage redirects via the
 * userID-null path; the kick mechanism owns the timer-driven logout.
 */
const WS_AUTH_REJECTED_CODE = 4401;

export class WebSocketRoutineRunnerHandle extends RoutineRunnerHandle {
  private connectionStatus = new BehaviorSubject<ConnectionStatus>({
    kind: 'constructing',
  });

  private failedConnectionAttempts = 0;

  private tokenRetries = 0;

  /**
   * Number of consecutive reconnect attempts since the last successful
   * authentication. Drives the exponential backoff in
   * `teardownAndReconnect`. Reset to 0 on `auth_result.success`.
   */
  private reconnectAttempts = 0;

  public constructor(private robotKind: 'simulated' | 'live') {
    super();

    this.connect();
  }

  private async connect() {
    // don't do anything when running on server
    if (typeof window === 'undefined') {
      return;
    }

    // don't do anything when disabled (e.g. in assembly mode)
    if (this.isDisabled) {
      log.info(
        `uncategorized`,
        'skipping routine-runner connection (disabled)',
      );

      return;
    }

    if (this.isDestroyed) {
      return;
    }

    const currentStatus = this.getConnectionStatus();

    // Don't update status to connecting if we're already in a failed state
    if (currentStatus.kind !== 'failure') {
      log.info(`uncategorized`, `Connecting...`);
      this.connectionStatus.next({ kind: 'connecting' });
    }

    const token = localStorage.getItem('feathers-jwt');

    if (!token) {
      this.tokenRetries += 1;

      if (this.tokenRetries >= MAX_TOKEN_RETRIES) {
        log.error(
          'auth.noToken',
          `No feathers-jwt after ${MAX_TOKEN_RETRIES} retries`,
        );

        this.connectionStatus.next({
          kind: 'failure',
          error: 'Authentication token not available',
        });

        return;
      }

      log.warn(
        'auth.noToken',
        'No feathers-jwt in localStorage, retrying in 1s',
      );

      await wait(1_000);
      this.connect();

      return;
    }

    this.tokenRetries = 0;

    const url = this.robotKind === 'simulated' ? 'vizbot-ws' : 'routine-runner';

    const ws = new WebSocket(`${API_ENDPOINT}${url}`);

    const connectingTimeoutID = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        log.info(`uncategorized`, 'Timed out while connecting');
        ws.close(4001, 'Connecting timeout');
        this.handleConnectionFailure();
      }
    }, 5_000);

    const destructors: Array<() => void> = [];

    // teardown is memoized so it is only called once
    const teardownAndReconnect = memoize(async () => {
      log.info(`uncategorized`, 'Teardown', { readyState: ws.readyState });

      if (ws.readyState !== ws.CLOSING && ws.readyState !== ws.CLOSED) {
        ws.close(4000, 'Teardown');
      }

      for (const destructor of destructors) {
        destructor();
      }

      const status = this.getConnectionStatus();

      if (status.kind !== 'connecting' && status.kind !== 'failure') {
        this.connectionStatus.next({ kind: 'disconnected' });
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then cap at 30s. The
      // first reconnect remains at 1s so brief-network-blip recovery is
      // unchanged from the previous constant-1s behaviour.
      const backoffMs = Math.min(
        RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
        RECONNECT_CAP_MS,
      );

      this.reconnectAttempts += 1;
      await wait(backoffMs);
      this.connect();
    });

    // Destroy the connection if nothing received for a while
    const scheduleAutoDisconnect = debounce(() => {
      log.info(`uncategorized`, 'Auto disconnect (nothing received for 5s)');
      teardownAndReconnect();
    }, 5_000);

    ws.onopen = () => {
      log.info(`uncategorized`, 'Connected');
      clearTimeout(connectingTimeoutID);

      try {
        if (token) {
          ws.send(
            JSON.stringify({
              type: 'auth',
              token,
              method: 'jwt',
            }),
          );
        }
      } catch (error) {
        log.warn('auth', 'Failed to send auth message', { error });
      }
    };

    ws.onmessage = (ev) => {
      const raw = typeof ev.data === 'string' ? ev.data : String(ev.data);

      try {
        const parsed = JSON.parse(raw);

        if (parsed.type === 'auth_result') {
          if (!parsed.success) {
            log.error('auth', 'WebSocket authentication failed', {
              error: parsed.error,
            });

            teardownAndReconnect();

            return;
          }

          // Auth succeeded — now safe to start sending data packets.
          // Registering onPacket flushes any packets that were buffered
          // during the auth handshake (e.g. getStateSnapshot requests).
          this.connectionStatus.next({ kind: 'connected' });
          this.failedConnectionAttempts = 0;
          this.reconnectAttempts = 0;

          if (this.arePacketsHandled()) {
            log.warn(
              `uncategorized`,
              'Multiple outgoing packet handlers being attached. Multiple peers may have been created, duplicating bandwidth',
            );
          }

          destructors.push(
            this.onPacket((packet) => {
              this.emitSBDevToolsEvent({
                kind: 'outgoingPacket',
                packet,
              });

              if (ws.readyState === WebSocket.OPEN) {
                ws.send(packet);
              }
            }),
          );

          return;
        }
      } catch {
        // Not JSON — fall through to normal handling
      }

      scheduleAutoDisconnect();
      this.receivePacket(raw);
    };

    destructors.push(
      this.onDestroy(() => {
        ws.close();
      }),
    );

    ws.onerror = () => {
      log.warn(`uncategorized`, 'Connection failed');
      this.handleConnectionFailure();
    };

    ws.onclose = (ev) => {
      // 4401 = auth rejected. Reconnecting would just be rejected again
      // (storming the rate limit). Surface the failure so PrivatePage can
      // redirect via the standard userID-null path. The session-expiry
      // timer (separate mechanism) owns the actual logout cascade.
      if (ev.code === WS_AUTH_REJECTED_CODE) {
        log.warn('auth.rejected', 'WebSocket closed with 4401', {
          reason: ev.reason,
        });

        // Cancel any pending `scheduleAutoDisconnect` debounce. Without
        // this, a debounce armed by an earlier `ws.onmessage` packet
        // (within the last 5 s) would still fire and call
        // `teardownAndReconnect()`, which bypasses this no-reconnect
        // guard and burns one extra auth attempt against the (still
        // expired) JWT.
        scheduleAutoDisconnect.cancel();

        this.connectionStatus.next({
          kind: 'failure',
          error: 'Session ended',
          reason: 'auth_rejected',
        });

        return;
      }

      teardownAndReconnect();
    };
  }

  private handleConnectionFailure() {
    this.failedConnectionAttempts += 1;

    if (this.failedConnectionAttempts >= MAX_FAILED_ATTEMPTS) {
      log.error(
        `uncategorized`,
        `Failed to connect after ${MAX_FAILED_ATTEMPTS} attempts`,
      );

      this.connectionStatus.next({
        kind: 'failure',
        error: `Failed to connect after ${MAX_FAILED_ATTEMPTS} attempts`,
      });
    }
  }

  public getName(): Promise<string> {
    return Promise.resolve('Live (Web Socket)');
  }

  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus.value;
  }

  public onConnectionChange(
    cb: (connectionStatus: ConnectionStatus) => void,
  ): () => void {
    const subscription = this.connectionStatus.subscribe(cb);

    return () => subscription.unsubscribe();
  }
}

function getWebSocketRoutineRunnerHandle(
  robotKind: 'simulated' | 'live',
): WebSocketRoutineRunnerHandle {
  return globalCache(
    `webSocketRoutineRunnerHandle.${robotKind}`,
    ({ reset }) => {
      const handle = new WebSocketRoutineRunnerHandle(robotKind);

      handle.onDestroy(() => reset());

      return handle;
    },
  );
}

export const getVizbotRoutineRunnerHandle = () => {
  return getWebSocketRoutineRunnerHandle('simulated');
};

export const getLiveRoutineRunnerHandle = () => {
  return getWebSocketRoutineRunnerHandle('live');
};
